import { Crop, ZoomOut, ZoomIn } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Slider, Stack } from '@mui/material';
import { useState } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { cropKivraImage } from './kivraImageCropConfig';

type KivraCropImageProps = {
  open: boolean;
  image: string;
  onComplete: (result) => void;
  onAbort: () => void;
};

export const KivraCropImage = (props: KivraCropImageProps) => {
  const { open, image, onComplete, onAbort } = props;
  const { t } = useTranslation(['common']);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const minZoom = 1;
  const maxZoom = 3;

  const handleZoom = (val: number) => {
    if (val > 0) {
      setZoom(Math.min(maxZoom, zoom + 0.1));
    } else {
      setZoom(Math.max(minZoom, zoom - 0.1));
    }
  };

  return (
    <Dialog open={open} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <Crop fontSize='small' sx={{ mr: 1 }} /> {t('common:cropImageHeader')}
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ position: 'relative', minHeight: '25rem' }}>
          <Box
            sx={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              bottom: '80px',
            }}
          >
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onCropComplete={(_, _croppedAreaPixels) => {
                setCroppedAreaPixels(_croppedAreaPixels);
              }}
              onZoomChange={setZoom}
            />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              bottom: '0',
              left: '50%',
              width: '50%',
              transform: 'translateX(-50%)',
              height: '80px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Stack spacing={2} direction='row' sx={{ mb: 1, width: '100%' }} alignItems='center'>
              <IconButton onClick={() => handleZoom(-1)} disabled={zoom === minZoom} size='large'>
                <ZoomOut sx={{ color: (thm) => thm.palette.grey[zoom === minZoom ? 200 : 700] }} />
              </IconButton>
              <Slider
                sx={{
                  "& .MuiSlider-markLabel[data-index='0']": {
                    transform: 'translateX(0)',
                  },
                  "& .MuiSlider-markLabel[data-index='1']": {
                    transform: 'translateX(-100%)',
                  },
                }}
                value={zoom}
                min={minZoom}
                max={maxZoom}
                step={0.1}
                size='small'
                onChange={(e, zoomValue) => setZoom(Number(zoomValue))}
                classes={{ root: 'slider' }}
              />
              <IconButton onClick={() => handleZoom(1)} disabled={zoom === maxZoom} size='large'>
                <ZoomIn sx={{ color: (thm) => thm.palette.grey[zoom === maxZoom ? 200 : 700] }} />
              </IconButton>
            </Stack>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color='primary' variant='outlined' onClick={() => onAbort()}>
          {t('common:button.cancel')}
        </Button>
        <Button
          color='primary'
          variant='contained'
          disableElevation
          onClick={() => onComplete(cropKivraImage(image, croppedAreaPixels, ConsoleHelper.log))}
        >
          {t('common:button.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
